import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight,
  CircleArrowRight,
  Award,
  Lightbulb,
  Cog,
  Headset,
  Wrench,
  Users,
  Rocket,
  Linkedin,
  Handshake,
} from "lucide-react";
import Lightfall from "@/components/Lightfall";
import SEO, { buildBreadcrumbJsonLd } from "@/components/SEO";
import { seoConfig } from "@/data/seoConfig";

const Counter = ({ target, suffix = "+" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 2000;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  return (
    <span ref={ref}>
      {value}
      {suffix}
    </span>
  );
};

const expertise = [
  {
    icon: Award,
    title: "Experience",
    desc: "We have a rich history of improving IT operations for businesses worldwide, supported by a deep well of industry knowledge and technical skills.",
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    desc: "Our three proprietary products have set new standards in the market, showcasing our commitment to cutting-edge solutions.",
  },
  {
    icon: Cog,
    title: "Capability",
    desc: "We've successfully placed over 3,000 high-quality professionals in critical IT roles, demonstrating our ability to meet diverse business needs.",
  },
];

const whyChoose = [
  {
    icon: Headset,
    title: "Client Support",
    desc: "We have supported over 500+ clients with our technology and talent acquisition services, ensuring their success and satisfaction.",
  },
  {
    icon: Wrench,
    title: "Technology Engagements",
    desc: "With more than 300 technology projects delivered, we have a proven track record of excellence and reliability.",
  },
  {
    icon: Users,
    title: "Staffing Solutions",
    desc: "Our staffing services have placed over 3,000 professionals in specialized roles, from Azure to Zlib, ensuring the right fit for every position.",
  },
  {
    icon: Rocket,
    title: "Innovation Support",
    desc: "We've helped over 100 startups with technology services, fostering innovation and growth in the tech ecosystem.",
  },
];

const team = [
  { name: "Ramana Krosuri", role: "President & CEO", linkedin: "https://www.linkedin.com/in/ramana-krosuri-12233027/" },
  { name: "Sunita Krosuri", role: "Chairwoman & CLO", linkedin: "https://www.linkedin.com/in/sunitakrosuri/" },
  { name: "Ashok Narayanan", role: "Chief Delivery Officer", linkedin: "https://www.linkedin.com/in/ashok-miti-34b8659b" },
  { name: "Sundaram Ramaswamy", role: "Senior Manager", linkedin: "https://www.linkedin.com/in/sundaram-ramaswamy-24ab601/" },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO {...seoConfig.about} jsonLd={[buildBreadcrumbJsonLd(seoConfig.about.crumbs)]} />
      <section data-testid="about-hero" className="relative bg-miti-navy pt-40 pb-32 hero-grain overflow-hidden">
        <div className="absolute inset-0">
          <Lightfall
            colors={["#A6C8FF", "#5227FF", "#FF9FFC"]}
            backgroundColor="#0A29FF"
            speed={0.5}
            streakCount={2}
            streakWidth={1}
            streakLength={1}
            density={0.6}
            twinkle={1}
            glow={1}
            backgroundGlow={0.5}
            zoom={3}
            opacity={1}
            mouseInteraction
            mouseStrength={0.5}
            mouseRadius={1}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <motion.nav
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-2.5 text-sm text-slate-300"
            data-testid="about-breadcrumb"
          >
            <a href="/" className="hover:text-white transition-colors" data-testid="breadcrumb-home">
              Home
            </a>
            <CircleArrowRight size={16} className="text-sky-400" />
            <span className="text-white font-medium">About Us</span>
          </motion.nav>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white"
          >
            About Us
          </motion.h1>
        </div>
        <svg
          viewBox="0 0 1000 100"
          preserveAspectRatio="none"
          aria-hidden="true"
          className="absolute bottom-0 left-0 w-full h-14 sm:h-20 fill-white"
        >
          <path d="M500,97C126.7,96.3,0.8,19.8,0,0v100l1000,0V1C1000,19.4,873.3,97.8,500,97z" />
        </svg>
      </section>

      <section data-testid="who-we-are-section" className="pt-24 lg:pt-12 pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">Who We Are</span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Pioneering Technology Solutions
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              At Millennium InfoTech Inc., we are dedicated to delivering world-class technology solutions. With
              over 20 years of experience, our team is committed to innovation, capability, and excellence. We
              specialize in Generative AI, Digital Transformation, Product Engineering, and Talent Acquisition,
              helping businesses enhance their IT operations and achieve their goals.
            </p>
            <p className="mt-4 text-base md:text-lg text-slate-500 leading-relaxed">
              Our expertise spans various industries, allowing us to provide tailored solutions that drive
              efficiency, foster innovation, and ensure sustainable growth. Partner with us to leverage advanced
              technology and stay ahead in a competitive market.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/About_us.jpg"
                alt="Millennium InfoTech team"
                className="w-full h-[380px] lg:h-[480px] object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -left-4 sm:-left-8 rounded-2xl bg-miti-blue px-8 py-6 shadow-xl text-center">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-sky-200">Years Experienced</p>
              <p className="mt-1 font-heading font-bold text-4xl text-white" data-testid="years-counter">
                <Counter target={20} />
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="vision-mission-section" className="pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-6">
          {[
            {
              title: "Our Vision",
              text: "At Millennium InfoTech Inc., our vision is to be the industry leader in Generative AI, driving innovation and pushing the boundaries of what's possible. We aim to harness the transformative power of AI to create global impact, revolutionizing the way businesses operate and people live their lives.",
              testId: "vision-card",
            },
            {
              title: "Our Mission",
              text: "Our mission is to empower customers with cutting-edge AI solutions, ensuring their success and growth. We strive for excellence in Generative AI, delivering sustainable value through continuous innovation and an unwavering commitment to ethical practices that benefit society.",
              testId: "mission-card",
            },
          ].map((card, i) => (
            <motion.div
              key={card.title}
              {...fadeUp}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              data-testid={card.testId}
              className="rounded-2xl bg-slate-50 border border-slate-100 p-9 lg:p-12 hover:border-miti-blue/30 transition-colors duration-300"
            >
              <h3 className="font-heading font-bold tracking-tight text-2xl sm:text-3xl text-slate-900">
                {card.title}
              </h3>
              <div className="mt-3 h-0.5 w-12 bg-miti-blue" />
              <p className="mt-6 text-base text-slate-500 leading-relaxed">{card.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section data-testid="expertise-section" className="py-24 lg:py-16 bg-slate-50/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">Our Expertise</span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
              Mastering the Art of <span className="text-miti-blue">Technology</span>
            </h2>
            <div className="mt-5 mx-auto h-0.5 w-12 bg-miti-blue" />
            <p className="mt-5 text-base md:text-lg text-slate-500">
              Our team excels in combining industry knowledge with advanced technical skills to deliver innovative
              solutions.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {expertise.map((e, i) => (
              <motion.div
                key={e.title}
                {...fadeUp}
                transition={{ duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                data-testid={`expertise-card-${i + 1}`}
                className="rounded-2xl border border-slate-200 bg-white p-9 hover:border-miti-blue/40 transition-colors duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-miti-blue/10 text-miti-blue flex items-center justify-center">
                  <e.icon size={22} strokeWidth={1.8} />
                </div>
                <h3 className="mt-6 font-heading font-semibold text-xl text-slate-900">{e.title}</h3>
                <p className="mt-3 text-sm md:text-base text-slate-500 leading-relaxed">{e.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="why-choose-section" className="py-24 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/Industry_Leaders.jpg"
                alt="Trusted by Industry Leaders"
                className="w-full h-[420px] lg:h-[560px] object-cover"
              />
            </div>
          </motion.div>
          <div>
            <motion.div {...fadeUp}>
              <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">Why Choose Us</span>
              <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-slate-900 leading-tight">
                Trusted by Industry Leaders
              </h2>
            </motion.div>
            <div className="mt-9 space-y-7">
              {whyChoose.map((w, i) => (
                <motion.div
                  key={w.title}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.55, delay: i * 0.08 }}
                  className="flex items-start gap-5"
                  data-testid={`why-choose-item-${i + 1}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-miti-blue/10 text-miti-blue flex items-center justify-center shrink-0">
                    <w.icon size={22} strokeWidth={1.8} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-slate-900">{w.title}</h3>
                    <p className="mt-1.5 text-sm md:text-base text-slate-500 leading-relaxed">{w.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="team-section" className="relative py-24 lg:py-16 bg-slate-50/70 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, #cbd5e1 1.5px, transparent 1.5px)",
            backgroundSize: "24px 24px",
            maskImage: "radial-gradient(480px 480px at 8% 15%, black, transparent 70%)",
            WebkitMaskImage: "radial-gradient(480px 480px at 8% 15%, black, transparent 70%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">Our Team</span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Meet the Minds Behind Our AI Innovation
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-500 leading-relaxed">
              Our team comprises seasoned professionals and passionate innovators dedicated to delivering top-notch
              IT solutions.
            </p>
            <p className="mt-3 text-base md:text-lg text-slate-500 leading-relaxed">
              Led by industry veterans with decades of experience, we bring confidence and a touch of fanaticism to
              every project.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, i) => (
              <motion.div
                key={member.name}
                {...fadeUp}
                transition={{ duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -8 }}
                data-testid={`team-card-${i + 1}`}
                className="group relative rounded-3xl bg-white border border-slate-200 overflow-hidden text-center shadow-sm hover:shadow-2xl hover:shadow-miti-blue/10 hover:border-miti-blue/30 transition-all duration-300"
              >
                <div className="h-16 bg-gradient-to-r from-miti-navy to-miti-blue" />
                <div className="px-8 pb-8">
                  <div className="mx-auto -mt-10 w-20 h-20 rounded-full bg-gradient-to-br from-sky-400 to-miti-blue text-white flex items-center justify-center font-heading font-bold text-2xl ring-4 ring-white shadow-lg shadow-miti-blue/20">
                    {member.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <h3 className="mt-5 font-heading font-semibold text-lg text-slate-900">{member.name}</h3>
                  <p className="mt-1 text-sm text-miti-blue font-medium">{member.role}</p>
                  <div className="mt-3 mx-auto h-px w-8 bg-slate-200 group-hover:w-12 group-hover:bg-miti-blue/40 transition-all duration-300" />
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`team-linkedin-${i + 1}`}
                    aria-label={`${member.name} on LinkedIn`}
                    className="mt-5 inline-flex items-center justify-center w-9 h-9 rounded-full bg-slate-100 text-slate-500 group-hover:bg-miti-blue group-hover:text-white transition-colors duration-300"
                  >
                    <Linkedin size={16} />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-cta-section" className="py-24 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Start a Conversation
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Ready to Transform Your Business?
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              Connect with us today to explore how our innovative technology solutions can drive your business
              forward. Let's innovate together.
            </p>
            <a
              href="/contact"
              data-testid="about-contact-button"
              className="group mt-9 inline-flex items-center gap-3 rounded-lg bg-miti-blue px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#005a96] transition-colors"
            >
              <Handshake size={18} />
              Contact Us
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/cta-bg.jpg"
                alt="Ready to Transform Your Business"
                className="w-full h-[320px] lg:h-[420px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AboutUs;
